/**
 * Map-driven geofence picker for the DPP rules editor.
 *
 * Mirrors the helipass Android UX:
 *   - civic-address autocomplete via Photon (OpenStreetMap)
 *   - OSM map tiles via Leaflet
 *   - draggable marker for the geofence centre
 *   - radius circle the operator can tune with a numeric input
 *
 * Emits a flat ``{ lat, lon, radius_m, address? }`` shape that drops
 * straight into a passport template rule's ``geo`` block.
 */
import { Box, Flex, Input, Stack, Text } from "@chakra-ui/react"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import { useEffect, useMemo, useRef, useState } from "react"
import {
  Circle,
  MapContainer,
  Marker,
  TileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet"

import { useSemanticColors } from "@/hooks/useSemanticColors"
import {
  type PhotonSuggestion,
  photonReverse,
  photonSearch,
} from "@/lib/photonGeocoder"

// react-leaflet ships icon assets via webpack-style URL resolution, which
// breaks under Vite. Hand-build the default icon so markers render.
const DEFAULT_ICON = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
})

export interface GeofenceValue {
  lat: number
  lon: number
  radius_m: number
  address?: string
}

interface GeofencePickerProps {
  value: GeofenceValue
  onChange: (next: GeofenceValue) => void
  /** Initial map height; the picker is otherwise responsive. */
  mapHeight?: number | string
}

// Re-centre the map whenever the external value changes (e.g. operator
// picked a suggestion). useMap() only works inside MapContainer.
function MapController({ lat, lon }: { lat: number; lon: number }) {
  const map = useMap()
  useEffect(() => {
    map.setView([lat, lon], map.getZoom() < 13 ? 14 : map.getZoom())
  }, [lat, lon, map])
  return null
}

// Click anywhere on the map to drop the marker there. Reverse-geocode
// the new point so the address text catches up.
function ClickToMove({
  onPick,
}: { onPick: (lat: number, lon: number) => void }) {
  useMapEvents({
    click: (e) => onPick(e.latlng.lat, e.latlng.lng),
  })
  return null
}

export function GeofencePicker({
  value,
  onChange,
  mapHeight = 280,
}: GeofencePickerProps) {
  const sc = useSemanticColors()
  const [query, setQuery] = useState(value.address ?? "")
  const [suggestions, setSuggestions] = useState<PhotonSuggestion[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const debounceRef = useRef<number | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Keep the address input in sync if the parent rewrites the value
  // (e.g. picking a different existing rule to edit).
  useEffect(() => {
    setQuery(value.address ?? "")
  }, [value.address])

  // Debounced autocomplete — Photon is a free public service, don't
  // spam it on every keystroke. Aborts in-flight requests when typing
  // continues so stale results never overwrite fresher ones.
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    if (query.trim().length < 3) {
      setSuggestions([])
      return
    }
    debounceRef.current = window.setTimeout(() => {
      abortRef.current?.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl
      photonSearch(query, { signal: ctrl.signal, limit: 6 }).then((r) => {
        if (!ctrl.signal.aborted) setSuggestions(r)
      })
    }, 350)
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
    }
  }, [query])

  const pickSuggestion = (s: PhotonSuggestion) => {
    const address = s.secondary ? `${s.primary}, ${s.secondary}` : s.primary
    setQuery(address)
    setShowSuggestions(false)
    onChange({ ...value, lat: s.lat, lon: s.lon, address })
  }

  const handleMapPick = async (lat: number, lon: number) => {
    onChange({ ...value, lat, lon })
    // Refresh the address line in the background; if reverse-geocoding
    // fails we keep whatever the user already typed.
    const reverse = await photonReverse(lat, lon)
    if (reverse) {
      setQuery(reverse)
      onChange({ ...value, lat, lon, address: reverse })
    }
  }

  const handleMarkerDragEnd = (e: L.LeafletEvent) => {
    const marker = e.target as L.Marker
    const ll = marker.getLatLng()
    handleMapPick(ll.lat, ll.lng)
  }

  const radius = Math.max(1, Number(value.radius_m) || 0)
  const centre: [number, number] = useMemo(
    () => [Number(value.lat) || 0, Number(value.lon) || 0],
    [value.lat, value.lon],
  )

  return (
    <Stack gap={2}>
      <Box>
        <Text fontSize="xs" color={sc.textMuted} mb={1}>
          Civic address
        </Text>
        {/*
         * Suggestions render inline ABOVE the input so the on-screen
         * keyboard on phones never hides them. They take real layout
         * space (no absolute positioning) which pushes the rest of the
         * picker — including the map — upward into the visible area.
         */}
        {showSuggestions && suggestions.length > 0 && (
          <Box
            mb={1}
            bg={sc.cardBg}
            border="1px solid"
            borderColor={sc.border}
            borderRadius="md"
            boxShadow="lg"
            maxH="220px"
            overflowY="auto"
          >
            {suggestions.map((s, i) => (
              <Box
                key={`${s.lat}-${s.lon}-${i}`}
                px={3}
                py={2}
                cursor="pointer"
                _hover={{ bg: sc.hoverBg }}
                onMouseDown={(e) => {
                  // mousedown fires before blur, so the suggestion list
                  // doesn't unmount before this click handler runs.
                  e.preventDefault()
                  pickSuggestion(s)
                }}
              >
                <Text fontSize="sm" color={sc.heading}>
                  {s.primary}
                </Text>
                {s.secondary && (
                  <Text fontSize="xs" color={sc.textMuted}>
                    {s.secondary}
                  </Text>
                )}
              </Box>
            ))}
          </Box>
        )}
        <Input
          size="sm"
          value={query}
          placeholder="1234 Main St, Montreal"
          onChange={(e) => {
            setQuery(e.target.value)
            setShowSuggestions(true)
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => {
            // Delay so onClick on a suggestion row still fires
            window.setTimeout(() => setShowSuggestions(false), 150)
          }}
        />
        <Text fontSize="xs" color={sc.textMuted} mt={1}>
          Addresses by Photon / © OpenStreetMap contributors
        </Text>
      </Box>

      <Flex gap={2} flexWrap="wrap">
        <Box flex="1" minW="100px">
          <Text fontSize="xs" color={sc.textMuted}>
            Latitude
          </Text>
          <Input
            type="number"
            step="any"
            size="sm"
            value={value.lat}
            onChange={(e) =>
              onChange({ ...value, lat: Number(e.target.value) })
            }
          />
        </Box>
        <Box flex="1" minW="100px">
          <Text fontSize="xs" color={sc.textMuted}>
            Longitude
          </Text>
          <Input
            type="number"
            step="any"
            size="sm"
            value={value.lon}
            onChange={(e) =>
              onChange({ ...value, lon: Number(e.target.value) })
            }
          />
        </Box>
        <Box flex="1" minW="100px">
          <Text fontSize="xs" color={sc.textMuted}>
            Radius (m)
          </Text>
          <Input
            type="number"
            min={1}
            size="sm"
            value={value.radius_m}
            onChange={(e) =>
              onChange({
                ...value,
                radius_m: Math.max(1, Number(e.target.value)),
              })
            }
          />
        </Box>
      </Flex>

      <Box
        h={typeof mapHeight === "number" ? `${mapHeight}px` : mapHeight}
        borderRadius="md"
        overflow="hidden"
        border="1px solid"
        borderColor={sc.border}
      >
        <MapContainer
          center={centre}
          zoom={14}
          style={{ width: "100%", height: "100%" }}
          scrollWheelZoom
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <MapController lat={centre[0]} lon={centre[1]} />
          <ClickToMove onPick={handleMapPick} />
          <Marker
            position={centre}
            icon={DEFAULT_ICON}
            draggable
            eventHandlers={{ dragend: handleMarkerDragEnd }}
          />
          <Circle
            center={centre}
            radius={radius}
            pathOptions={{ color: "#34a1dd", fillOpacity: 0.15 }}
          />
        </MapContainer>
      </Box>
    </Stack>
  )
}
